import React from "react";
import { Alert, Button, Input, Message, Modal, Space, Switch, Tag, Typography } from "@arco-design/web-react";
import { IconLaunch, IconRefresh, IconEmail } from "@arco-design/web-react/icon";
import { useAuth0 } from "@auth0/auth0-react";
import { PostApi } from "../../Api/AuthenticatedApi";
import DnsRecordsTable from "./DnsRecordsTable";

const { Text, Paragraph, Title } = Typography;

// stepsFor groups a domain's records into the walkthrough's steps, in the
// order to do them. Pure, so the grouping is tested without rendering.
export function stepsFor(records) {
  const of = (...purposes) => (records || []).filter((r) => purposes.includes(r.purpose));
  const steps = [];
  const ownership = of("ownership");
  const dkim = of("dkim");
  const dmarc = of("dmarc");
  const bounce = of("mail_from_mx", "mail_from_spf");
  if (ownership.length) {
    steps.push({ key: "ownership", title: "Add the ownership record", required: true, records: ownership,
      why: "This shows the domain belongs to your account." });
  }
  if (dkim.length) {
    steps.push({ key: "dkim", title: `Add the ${dkim.length} signing records`, required: true, records: dkim,
      why: "These let Amazon sign your mail as your domain, so inboxes trust it." });
  }
  if (dmarc.length) {
    steps.push({ key: "dmarc", title: "Strongly recommended: add a DMARC record", required: false, records: dmarc,
      why: "Gmail and Yahoo expect one from anyone sending to many people, and without it your mail may be rejected or sent to spam. If your domain already has a DMARC record, keep that one: don't add a second." });
  }
  if (bounce.length) {
    steps.push({ key: "bounce", title: "Recommended: add the bounce records", required: false, records: bounce,
      why: "Not needed to send, but they help your mail reach the inbox: bounced mail comes back through Amazon, and your domain passes one more check (SPF)." });
  }
  return steps;
}

// improvements lists what a verified domain still lacks for the best inbox
// placement, most important first. Pure, so it's tested without rendering.
export function improvements(domain) {
  if (!domain || !domain.verified_date) return [];
  const out = [];
  if (!domain.dmarc_found) {
    out.push({ key: "dmarc", label: "Add a DMARC record",
      why: "Gmail and Yahoo expect one; without it your mail may be rejected or sent to spam." });
  }
  const bounce = (domain.records || []).find((r) => r.purpose === "mail_from_mx");
  if (bounce && bounce.status !== "ok") {
    out.push({ key: "bounce", label: "Add the 2 bounce records",
      why: "Your domain then passes one more check (SPF), for the best inbox placement." });
  }
  return out;
}

// Checklist shows a verified domain's extras: done, or what to add.
function Checklist({ domain }) {
  const missing = improvements(domain);
  const bounce = (domain.records || []).find((r) => r.purpose === "mail_from_mx");
  const item = (done, label, why) => (
    <div key={label} style={{ display: "flex", gap: 8, alignItems: "baseline", marginTop: 4 }}>
      <Tag color={done ? "green" : "orange"} size="small">{done ? "Done" : "To do"}</Tag>
      <span><Text bold={!done}>{label}</Text>{why ? <Text type="secondary"> {why}</Text> : null}</span>
    </div>
  );
  return (
    <div style={{ marginBottom: 12 }}>
      <Title heading={6} style={{ marginTop: 4 }}>
        {missing.length ? `Verified. ${missing.length} thing${missing.length === 1 ? "" : "s"} left for the best inbox placement` : "Verified, with everything for the best inbox placement"}
      </Title>
      {item(true, "Verified: any address on this domain can send")}
      {item(!!domain.dmarc_found, domain.dmarc_found ? "DMARC record" : "Add a DMARC record (strongly recommended)",
        domain.dmarc_found ? "" : "Gmail and Yahoo expect one; without it your mail may be rejected or sent to spam.")}
      {bounce && item(bounce.status === "ok", bounce.status === "ok" ? "Bounce records" : "Add the 2 bounce records",
        bounce.status === "ok" ? "" : "Your domain then passes one more check (SPF).")}
      {missing.length > 0 && (
        <Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0 }}>The records to add are in the table below.</Paragraph>
      )}
    </div>
  );
}

function StatusTag({ record }) {
  if (record.status === "ok") return <Tag color="green">Found</Tag>;
  if (record.status === "failed") return <Tag color="red">Failed</Tag>;
  if (!record.required && !record.strongly_recommended) return <Tag>Not found yet</Tag>;
  return <Tag color="orange">Not found yet</Tag>;
}

// Record is one record to add, labeled the way the DNS provider labels its
// boxes. Also used by the spy-domain setup.
export function Record({ record, guide }) {
  const row = (label, value, copy) => (
    <div style={{ display: "flex", gap: 8, alignItems: "baseline", flexWrap: "wrap" }}>
      <Text type="secondary" style={{ minWidth: 150 }}>{label}</Text>
      {copy ? <Text code copyable style={{ wordBreak: "break-all" }}>{value}</Text> : <Text>{value}</Text>}
    </div>
  );
  return (
    <div style={{ border: "1px solid var(--color-border-2)", borderRadius: 4, padding: "8px 12px", marginTop: 8 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Text bold>{record.type} record</Text>
        <StatusTag record={record} />
      </div>
      {row("Type", record.type)}
      {row(guide.host_label, record.host, true)}
      {row(guide.value_label, record.value, true)}
      {record.priority ? row("Priority", String(record.priority)) : null}
    </div>
  );
}

// EmailStepsDialog sends the walkthrough to whoever manages the domain's DNS.
function EmailStepsDialog({ domain, open, onClose }) {
  const { getAccessTokenSilently } = useAuth0();
  const [email, setEmail] = React.useState("");
  const [note, setNote] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState("");

  const send = () => {
    setBusy(true);
    setError("");
    PostApi(getAccessTokenSilently, `/api/settings/sending-domains/${domain.id}/email-instructions`,
      () => {
        setBusy(false);
        Message.success(`Sent the steps to ${email.trim()}. Their replies come to you.`);
        setEmail("");
        setNote("");
        onClose();
      },
      () => {},
      (err) => { setBusy(false); setError((err && err.message) || "Failed to send the steps"); },
      { email: email.trim(), note });
  };

  return (
    <Modal
      visible={open}
      title="Email these steps"
      okText="Send"
      onOk={send}
      confirmLoading={busy}
      okButtonProps={{ disabled: !email.trim() }}
      onCancel={() => { setError(""); onClose(); }}
    >
      <Paragraph style={{ marginTop: 0 }}>
        We'll email the steps and the records for {domain.domain} to whoever manages your DNS, such as
        your web person or IT admin. Their replies come to you.
      </Paragraph>
      <Input placeholder="admin@yourbusiness.com" value={email} onChange={setEmail} onPressEnter={send} />
      <Input.TextArea
        style={{ marginTop: 8 }}
        placeholder="A note to them (optional)"
        value={note}
        onChange={setNote}
        autoSize={{ minRows: 2, maxRows: 5 }}
      />
      {error && <div style={{ fontSize: 12, color: "rgb(var(--red-6))", marginTop: 6 }}>{error}</div>}
    </Modal>
  );
}

// DnsSetupGuide walks an org through adding its domain's DNS records at its
// DNS provider, step by step. A verified domain shows just the records.
export default function DnsSetupGuide({ domain, onCheck, checking }) {
  const [emailOpen, setEmailOpen] = React.useState(false);
  const [asTable, setAsTable] = React.useState(false);
  const guide = domain.guide || { name: "your DNS provider", steps: [], host_label: "Host", value_label: "Value", tips: [] };
  const warnings = domain.dns_warnings || [];

  if (domain.verified_date || asTable) {
    return (
      <div>
        {domain.verified_date && <Checklist domain={domain} />}
        {domain.verified_date && warnings.filter((w) => w.code !== "no_dmarc").map((w, i) => (
          <Alert key={w.code + i} type="warning" style={{ marginBottom: 8 }} title="Something to fix" content={w.message} />
        ))}
        <DnsRecordsTable
          records={domain.records}
          controls={!domain.verified_date && (
            // The same switch as in the guide, so it's found where it was.
            <>
              <Switch size="small" checked={asTable} onChange={setAsTable} />
              <Text type="secondary" style={{ fontSize: 13, marginRight: 12 }}>Show as a table</Text>
            </>
          )}
        />
      </div>
    );
  }

  const steps = stepsFor(domain.records);
  let n = 1;
  return (
    <div style={{ maxWidth: 900 }}>
      {warnings.map((w, i) => (
        <Alert key={w.code + i} type="warning" style={{ marginBottom: 8 }} title="Something to fix" content={w.message} />
      ))}

      <Title heading={6} style={{ marginTop: 4 }}>{n++}. Open your DNS settings at {guide.name}</Title>
      {guide.provider ? (
        <Paragraph type="secondary" style={{ marginBottom: 4 }}>{domain.domain}'s DNS is managed at {guide.name}.</Paragraph>
      ) : null}
      <ol style={{ marginTop: 4, paddingLeft: 20 }}>
        {(guide.steps || []).map((s) => <li key={s}>{s}</li>)}
      </ol>
      {guide.url && (
        <Button type="outline" size="small" icon={<IconLaunch />} href={guide.url} target="_blank" rel="noreferrer">
          Open {guide.name}
        </Button>
      )}

      {steps.map((step) => (
        <div key={step.key}>
          <Title heading={6} style={{ marginTop: 20 }}>{n++}. {step.title}</Title>
          <Paragraph type="secondary" style={{ marginBottom: 0 }}>{step.why}</Paragraph>
          {step.records.map((r) => <Record key={r.purpose + r.name + r.value} record={r} guide={guide} />)}
        </div>
      ))}

      <Title heading={6} style={{ marginTop: 20 }}>{n++}. Check your records</Title>
      <Paragraph type="secondary" style={{ marginBottom: 8 }}>
        Once they're added, press Check now. DNS changes can take from a few minutes to a few hours to
        show up; we keep checking by ourselves, and the domain is verified as soon as the required
        records are found.
      </Paragraph>
      <Button type="primary" size="small" icon={<IconRefresh />} loading={checking} onClick={onCheck}>Check now</Button>

      {(guide.tips || []).length > 0 && (
        <Alert
          type="info"
          style={{ marginTop: 20 }}
          title={`Tips for ${guide.name}`}
          content={<ul style={{ margin: 0, paddingLeft: 18 }}>{guide.tips.map((t) => <li key={t}>{t}</li>)}</ul>}
        />
      )}

      <div style={{ marginTop: 20, paddingTop: 12, borderTop: "1px solid var(--color-border-2)" }}>
        <Space wrap>
          <Text>Someone else manages your DNS?</Text>
          <Button size="small" icon={<IconEmail />} onClick={() => setEmailOpen(true)}>Email these steps to them</Button>
          <Switch size="small" checked={asTable} onChange={setAsTable} />
          <Text type="secondary">Show as a table</Text>
        </Space>
      </div>
      <EmailStepsDialog domain={domain} open={emailOpen} onClose={() => setEmailOpen(false)} />
    </div>
  );
}
